import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { roleSchema, rolesResponseSchema, type RoleWire } from '@forecastify/shared';
import { useSession } from '../../session';
import { useT } from '../../i18n';
import { ScreenState, failureOf, type ScreenFailure } from '../screen-state';
import { useGoBack, useWorkspaceNavigate } from '../../workspace-path';
import { useFirstLoad } from '../use-first-load';
import { PermissionsScreen } from './permissions-screen';

/**
 * The Jogosultságok screen, with its data.
 *
 * ⚠️ Same split as the account screen: the screen renders props, this fetches. And the response is
 * parsed with the shared schema rather than cast — `as RoleWire[]` on a `fetch` result is a lie the
 * compiler believes, and the first symptom would be `undefined` inside a render rather than one
 * legible failure at the boundary.
 */
export function PermissionsRoute() {
  const session = useSession();
  /**
   * ⚠️⚠️ **THE STEP IS THE ADDRESS — item 62.** `/settings/permissions` is the list;
   * `/settings/permissions/:roleId` is that level's page. R-8: a level somebody is arguing about
   * over chat is a link, and the browser's Back is the same control as the header's.
   *
   * ⚠️ **R-38 · `go`, not `navigate`** — it takes a workspace-relative path and supplies the first
   * segment, which is what `eslint.config.mjs` bans a bare `go('/tasks')` to enforce.
   */
  const params = useParams();
  const go = useWorkspaceNavigate();
  const goBack = useGoBack();
  const t = useT();
  const [roles, setRoles] = useState<readonly RoleWire[]>([]);
  const [failure, setFailure] = useState<ScreenFailure | null>(null);
  /* ⚠️ A refusal from create or delete, shown in the dialog that caused it. */
  const [serverError, setServerError] = useState<string | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    /* Same three states as the account route, and for the same reason: a silent `return` here is a
     * permanent spinner. */
    if (session.workspaceId === null) {
      setFailure({ kind: 'no-workspace' });
      return;
    }

    const response = await session.request('/permissions/roles').catch(() => null);
    if (response === null) {
      setFailure({ kind: 'error', code: 'unavailable' });
      return;
    }
    if (!response.ok) {
      setFailure(await failureOf(response));
      return;
    }

    const parsed = rolesResponseSchema.safeParse(await response.json());
    if (!parsed.success) {
      setFailure({ kind: 'error', code: 'validation_failed' });
      return;
    }

    setFailure(null);
    setRoles(parsed.data.data);
  }, [session]);

  /* First load draws skeleton rows — `use-first-load.ts`. */
  const loading = useFirstLoad(reload);

  return (
    <ScreenState failure={failure} onRetry={() => void reload()}>
      <PermissionsScreen
        loading={loading}
        roles={roles}
        selectedId={params.roleId}
        onSelect={(roleId) => {
          go(`/settings/permissions/${roleId}`);
        }}
        /* ⚠️ Back to where the role was opened from; the role list only with no past (14 Oct). */
        onBack={() => {
          goBack('/settings/permissions');
        }}
        onSave={async (roleId, draft, version) => {
          await session.request(`/permissions/roles/${roleId}`, {
            method: 'PUT',
            body: JSON.stringify({
              grants: draft.grants,
              /* The screen keeps field rules as a `resource.field` map; the wire wants a list. One
               * conversion, here, so neither side has to know the other's shape. */
              fieldRules: Object.entries(draft.fieldRules).map(([key, access]) => {
                const [resource, ...rest] = key.split('.');
                return { resource: resource ?? '', field: rest.join('.'), access };
              }),
              version,
            }),
          });
          await reload();
        }}
        serverError={serverError}
        /**
         * ⚠️ **Resolves to the new level's id, or `null` on a refusal.** The dialog uses that to
         * decide whether to close: a refused create keeps the typed name, and `serverError` above
         * carries the sentence. Returning `void` would make every refusal look like a success.
         */
        onCreate={async (name, copyFromRoleId) => {
          setServerError(null);
          const response = await session
            .request('/permissions/roles', {
              method: 'POST',
              body: JSON.stringify({ name, copyFromRoleId }),
            })
            .catch(() => null);

          if (response?.ok !== true) {
            const body = (await response?.json().catch(() => null)) as {
              error?: { code?: string; details?: { code?: string }[] };
            } | null;
            setServerError(
              body?.error?.details?.[0]?.code === 'taken'
                ? t('permissions.levels.error.taken')
                : t('error.unavailable'),
            );
            return null;
          }

          const created = roleSchema.safeParse(await response.json());
          await reload();
          /* ⚠️ A parse failure is not a failed create — the level exists. The list has just
             reloaded, so the honest answer is *made, but I cannot open it*. */
          return created.success ? created.data.id : null;
        }}
        onDelete={async (roleId) => {
          setServerError(null);
          const response = await session
            .request(`/permissions/roles/${roleId}`, { method: 'DELETE' })
            .catch(() => null);
          if (response?.ok !== true) {
            const body = (await response?.json().catch(() => null)) as {
              error?: { details?: { code?: string }[] };
            } | null;
            setServerError(
              body?.error?.details?.[0]?.code === 'in_use'
                ? t('permissions.levels.error.inUse')
                : t('error.unavailable'),
            );
            return;
          }
          await reload();
        }}
      />
    </ScreenState>
  );
}
